import React, {createContext, useCallback, useContext, useMemo, useRef, useState} from 'react';
import {DateUtils} from "../../../core";

export interface SavedComponentState<T = string> {
    name: string;
    version: string;
    modifiedDateTime: string;
    data: T;
}

export interface ComponentToSaveState<T = string> {
    name: string,
    version: string,
    data: T,
}

/**
 * A serializer converts the stored string data into some type.
 */
export type SavedComponentStateSerializer<T> = (data: string) => T;

/**
 * A Stringifier converts the data into a string.
 */
export type SavedComponentStateStringifier<T> = (data: T) => string;

type FetchSavedComponents = (abortController?: AbortController) => Promise<SavedComponentState[]>;

type SaveComponentStates = (states: ComponentToSaveState[], abortController?: AbortController) => Promise<void>;

type SaveComponentState = (state: ComponentToSaveState, abortController?: AbortController) => Promise<void>;


interface SavedComponentsStateCacheContextType {
    /**
     * Returns the list of components – either from cache or by triggering a refresh.
     */
    getComponentsSavedState: FetchSavedComponents;
    /**
     * Saves the provided state values in the cache.
     * For each entry, if name and version matches with the provided data, replaces the data otherwise adds a new data.
     */
    saveComponentStates: SaveComponentStates;
    /**
     * Saves the provided state values in the server and in cache.
     */
    saveComponentState: SaveComponentState;
    /**
     * Forces a refresh of the cache by fetching the components from the server.
     */
    refreshSavedComponentsState: FetchSavedComponents;
}

const SavedComponentsStateCacheContext = createContext<SavedComponentsStateCacheContextType | undefined>(undefined);


export type SaveComponentStateRequest = Omit<SavedComponentState, 'modifiedDateTime'> & { applicationName: string };

type GetComponentsSavedStateFromServer = (request: { applicationName: string }, abortController?: AbortController) => Promise<SavedComponentState[]>;
type SaveComponentStateInServer = (component: SaveComponentStateRequest, abortController?: AbortController) => Promise<boolean>;

export interface SavedComponentsStateProviderProps {
    getComponentsSavedState: GetComponentsSavedStateFromServer;
    saveComponentState: SaveComponentStateInServer;
    cacheDuration?: number;
    applicationName: string;
}

export const SavedComponentsStateProvider: React.FC<React.PropsWithChildren<SavedComponentsStateProviderProps>> = (props) => {
    const {getComponentsSavedState: getComponentsSavedStateFromServer, saveComponentState: saveComponentStateInServer} = props;

    const [_cache, setCache] = useState<SavedComponentState[]>();
    const cacheRef = useRef(_cache);
    cacheRef.current = _cache;

    /** Holds the timestamp of the last fetch. */
    const lastFetchedRef = useRef(0);
    /** Holds the promise when an API call is in progress. */
    const fetchPromiseRef = useRef<Promise<SavedComponentState[]> | null>(null);

    const defaultCacheDuration = useMemo(() => 60 * 60 * 1000, []);
    const cacheDuration = useMemo(() => props.cacheDuration || defaultCacheDuration, [props.cacheDuration, defaultCacheDuration]);

    /**
     * Forces a refresh of the cache by fetching the components from the server.
     */
    const refreshSavedStateFromServer = useCallback<FetchSavedComponents>(async (abortController) => {
        // If there is already a fetch in progress, return that promise.
        if (fetchPromiseRef.current) {
            return fetchPromiseRef.current;
        }
        const promise = new Promise<SavedComponentState[]>(async (r) => {
            try {
                const data = await getComponentsSavedStateFromServer({applicationName: props.applicationName}, abortController);
                setCache(data);
                lastFetchedRef.current = Date.now();
                r(data);
            } finally {
                fetchPromiseRef.current = null;
            }
        });
        fetchPromiseRef.current = promise;
        return promise;
    }, [getComponentsSavedStateFromServer, props.applicationName]);

    /**
     * Returns the list of components – either from cache or by triggering a refresh.
     */
    const getComponentsSavedState = useCallback<FetchSavedComponents>(async (abortController) => {
        const cache = cacheRef.current;
        const now = Date.now();
        let data: SavedComponentState[];
        if (!cache || (now - lastFetchedRef.current) > cacheDuration) {
            data = await refreshSavedStateFromServer(abortController);
        } else {
            data = cache;
        }
        return data.map(e => e);
    }, [refreshSavedStateFromServer, cacheDuration]);

    /**
     * Saves the provided state values in the server and in cache.
     */
    const saveComponentState = useCallback<SaveComponentState>(async (state, abortController) => {
        const request: SaveComponentStateRequest = {
            applicationName: props.applicationName,
            name: state.name,
            version: state.version,
            data: state.data,
        }
        const resultFlag = await saveComponentStateInServer(request, abortController);
        if (resultFlag) {
            setCache(prevState => {
                const newState = prevState ?? [];
                const found = newState.find(e => e.name === state.name && e.version === state.version);
                if (found) {
                    const index = newState.indexOf(found);
                    newState[index] = {...found, data: state.data, modifiedDateTime: DateUtils.serverNow().format(DateUtils.dateTimeFormat)};
                } else {
                    newState.push({
                        ...state,
                        modifiedDateTime: DateUtils.serverNow().format(DateUtils.dateTimeFormat),
                    });
                }
                return newState.map(e => e);
            })
        }
    }, [props.applicationName, saveComponentStateInServer])

    /**
     * Saves the provided state values in the cache.
     * For each entry, if name and version matches with the provided data, replaces the data otherwise adds a new data.
     */
    const saveComponentStates = useCallback<SaveComponentStates>(async (states) => {
        const promises = [];
        for (const state of states) {
            promises.push(saveComponentState(state));
        }
        await Promise.all(promises);
    }, [saveComponentState])

    const cacheContextValue = useMemo<SavedComponentsStateCacheContextType>(() => ({
        getComponentsSavedState: getComponentsSavedState,
        refreshSavedComponentsState: refreshSavedStateFromServer,
        saveComponentStates: saveComponentStates,
        saveComponentState: saveComponentState,
    }), [getComponentsSavedState, refreshSavedStateFromServer, saveComponentState, saveComponentStates]);

    return (
        <SavedComponentsStateCacheContext.Provider value={cacheContextValue}>
            {props.children}
        </SavedComponentsStateCacheContext.Provider>
    );
};

/**
 * The hook to access the saved components state cache.
 */
const useSavedComponentsStateCache = (): SavedComponentsStateCacheContextType => {
    const context = useContext(SavedComponentsStateCacheContext);
    if (!context) {
        throw new Error('useSavedComponentsStateCache must be used within a SavedComponentsStateProvider');
    }
    return context;
};


interface UseSavedComponentsStateOptions<T = string> {
    name: string;
    version?: string;
    serializer?: SavedComponentStateSerializer<T>;
    stringifier?: SavedComponentStateStringifier<T>;
    versionComparator?: (versionA: String, versionB: string) => number;
}

export interface SavedComponentsStateResult<T> {
    /**
     * Finds a single entry matching the name and (optional) version.
     */
    find(abortController?: AbortController, version?: string): Promise<SavedComponentState<T> | undefined>;

    /**
     * Returns all entries matching the name.
     */
    all(abortController?: AbortController): Promise<SavedComponentState<T>[]>;

    /**
     * Returns the highest version entry that usually equates to the most updated entry.
     */
    highestVersion(abortController?: AbortController): Promise<SavedComponentState<T> | undefined>;

    /**
     * Returns the entry associated with the provided version in this component.
     */
    currentVersion(abortController?: AbortController): Promise<SavedComponentState<T> | undefined>;

    /**
     * Filters entries matching the name using the provided predicate.
     */
    filter(predicate: (entry: SavedComponentState<T>) => boolean, abortController?: AbortController): Promise<SavedComponentState<T>[]>;

    /**
     * Saves the provided state in the server.
     */
    save(stateOrCallback: T | ((prevState: T | undefined) => T), abortController?: AbortController, version?: string): Promise<void>;
}

/**
 * A hook to access the saved components state.
 * @param name                      The name of the component.
 * @param version                   The version of the component.
 * @param serializerOverride        The serializer to convert the stored string data into some type.
 * @param stringifierOverride       The stringifier to convert the data into a string.
 * @param versionComparatorOverride The comparator of the versions to determine the latest version.
 */
const useSavedComponentsState = <T = string>({
                                                 name,
                                                 version,
                                                 serializer: serializerOverride,
                                                 stringifier: stringifierOverride,
                                                 versionComparator: versionComparatorOverride,
                                             }: UseSavedComponentsStateOptions<T>): SavedComponentsStateResult<T> => {
    const {getComponentsSavedState, saveComponentState} = useSavedComponentsStateCache();

    /**
     * The default serializer.
     */
    const defaultSerializer = useCallback<SavedComponentStateSerializer<T>>(data => JSON.parse(data), []);

    /**
     * The serializer to use for the data.
     */
    const serializer = useMemo<SavedComponentStateSerializer<T>>(() => serializerOverride ?? defaultSerializer, [serializerOverride, defaultSerializer]);

    /**
     * The default stringifier.
     */
    const defaultStringifier = useCallback<SavedComponentStateStringifier<T>>(data => JSON.stringify(data), []);

    /**
     * The stringifier to use for the data.
     */
    const stringifier = useMemo<SavedComponentStateStringifier<T>>(() => stringifierOverride ?? defaultStringifier, [stringifierOverride, defaultStringifier]);

    /**
     * The default version comparator.
     */
    const defaultVersionComparator = useCallback<Required<UseSavedComponentsStateOptions>['versionComparator']>((versionA, versionB) => {
        const firstNum = Number(versionA);
        const secondNum = Number(versionB);
        if (!Number.isNaN(firstNum) && !Number.isNaN(secondNum))
            return secondNum - firstNum;
        return versionA.localeCompare(versionB);
    }, [])

    /** The version comparator that determines the current version. */
    const versionComparator = useMemo<Required<UseSavedComponentsStateOptions>['versionComparator']>(() => versionComparatorOverride ?? defaultVersionComparator, [versionComparatorOverride, defaultVersionComparator]);

    /**
     * Applies the serializer to the data if it is present.
     * @param entry     The entry to apply the serializer to.
     */
    const applySerializer = useCallback((entry: SavedComponentState): SavedComponentState<T> => {
        if (!entry.data) {
            return entry as SavedComponentState<T>;
        }
        return {...entry, data: serializer(entry.data)} as SavedComponentState<T>;
    }, [serializer]);

    /**
     * Finds an entry matching the name and the provided (or preset) version.
     */
    const find = useCallback<SavedComponentsStateResult<T>['find']>(async (abortController, versionOverride) => {
        const data = await getComponentsSavedState(abortController);
        const targetVersion = versionOverride ?? version;
        if (!targetVersion) {
            throw new Error('find method of useSavedComponentsState must have a version argument if the hook itself did not receive one.');
        }
        const found = data.find(e => e.name === name && e.version === targetVersion);
        return found ? applySerializer(found) : undefined;
    }, [getComponentsSavedState, version, applySerializer, name]);

    /**
     * Returns all entries matching the given name.
     */
    const all = useCallback<SavedComponentsStateResult<T>['all']>(async (abortController) => {
        const data = await getComponentsSavedState(abortController);
        return data.filter(e => e.name === name).map(applySerializer);
    }, [getComponentsSavedState, applySerializer, name]);

    /**
     * Returns entries matching the name that satisfy the provided predicate.
     */
    const filter = useCallback<SavedComponentsStateResult<T>['filter']>(async (predicate, abortController) => {
        const data = await getComponentsSavedState(abortController);
        return data.filter(e => e.name === name).map(applySerializer).filter(predicate);
    }, [getComponentsSavedState, applySerializer, name]);

    /**
     * Returns the highest version entry that usually equates to the most updated entry.
     */
    const highestVersion = useCallback<SavedComponentsStateResult<T>['highestVersion']>(async (abortController) => {
        const data = await getComponentsSavedState(abortController);
        const entries = data.filter(e => e.name === name);
        entries.sort((a, b) => versionComparator(a.version, b.version)).reverse();
        return entries.length ? applySerializer(entries[0]) : undefined;
    }, [getComponentsSavedState, applySerializer, name, versionComparator]);

    /**
     * Returns the entry associated with the provided version in this component.
     */
    const currentVersion = useCallback<SavedComponentsStateResult<T>['currentVersion']>(async (abortController) => {
        const data = await getComponentsSavedState(abortController);
        if (!version) {
            throw new Error('To use the currentVersion of useSavedComponentsState, you must provide a non-empty version argument to this hook');
        }
        const entry = data.find(e => e.name === name && e.version === version);
        return entry ? applySerializer(entry) : undefined;
    }, [getComponentsSavedState, applySerializer, name, version]);


    /**
     * Saves the provided entry into the server.
     */
    const save = useCallback<SavedComponentsStateResult<T>['save']>(async (stateOrCallback, abortController, versionOverride) => {
        const targetVersion = versionOverride ?? version;
        if (!targetVersion) {
            throw new Error('find method of useSavedComponentsState must have a version argument if the hook itself did not receive one.');
        }
        let data: T;
        if (typeof stateOrCallback === 'function') {
            const entry = await find(abortController, targetVersion);
            data = typeof stateOrCallback === 'function' ? (stateOrCallback as Function)(entry) : stateOrCallback;
        } else {
            data = stateOrCallback;
        }
        const stateToSave: ComponentToSaveState = {
            name: name,
            version: targetVersion,
            data: stringifier(data)
        };
        await saveComponentState(stateToSave, abortController);
    }, [find, saveComponentState, stringifier, version, name]);

    return {
        find: find,
        all: all,
        filter: filter,
        save: save,
        highestVersion: highestVersion,
        currentVersion: currentVersion,
    };
}
export default useSavedComponentsState;